import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { usePermissionsContext } from "../permissions";
import { permissionIdentity } from "../permissions/permission-identity";
import RemoteErrorBoundary from "../error-boundary";
import { Alert, Snackbar } from "@mui/material";
import { ArtButton } from "@art/shared-ui";

const Runtime = lazy(() => import("art_agentlab/PlaygroundRuntime"));

function PlaygroundFailure({ onRetry }: { onRetry: () => void }) {
  return (
    <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
      <Alert
        severity="error"
        variant="filled"
        sx={{ maxWidth: 480, alignItems: "center" }}
        action={
          <ArtButton
            color="inherit"
            variant="text"
            label="Reopen"
            onClick={onRetry}
          />
        }
      >
        Playground stopped unexpectedly. Reopen to start a new workspace; other
        pages are unaffected.
      </Alert>
    </Snackbar>
  );
}

/** Above Outlet: route changes must never own the lifetime of test transports. */
export default function PlaygroundRuntimeLoader() {
  const { pathname } = useLocation();
  const [visited, setVisited] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const auth = useSelector((s: RootState) => s.auth);
  const app = useSelector((s: RootState) => s.appstate);
  const permissions = usePermissionsContext();
  const onPlayground = pathname.startsWith("/agent-builder/playground");
  useEffect(() => {
    if (onPlayground) setVisited(true);
  }, [onPlayground]);
  const allowed =
    !permissions.loading &&
    permissions.hasAttribute("AGENT_LAB", "PLAYGROUND", "ViewPlayground");
  const identity = permissionIdentity(auth.tenantName, auth.token);
  const scope = JSON.stringify([identity, app.project_key, app.environment]);
  if (
    (!visited && !onPlayground) ||
    !allowed ||
    !identity ||
    !app.project_key ||
    !app.environment
  )
    return null;
  return (
    <RemoteErrorBoundary
      key={`${scope}:${attempt}`}
      remote="art_agentlab"
      exposedModule="art_agentlab/PlaygroundRuntime"
      fallback={() => (
        <PlaygroundFailure onRetry={() => setAttempt((value) => value + 1)} />
      )}
    >
      <Suspense fallback={null}>
        <Runtime
          canExecuteAgent={permissions.hasAttribute(
            "AGENT_LAB",
            "PLAYGROUND",
            "ExecutePlaygroundAgent",
          )}
          canExecuteOrchestrator={permissions.hasAttribute(
            "AGENT_LAB",
            "PLAYGROUND",
            "ExecutePlaygroundOrchestrator",
          )}
        />
      </Suspense>
    </RemoteErrorBoundary>
  );
}
